/**
 * F13 Task T26 — Admin read-only Members tab.
 *
 * Rendered as the 5th tab of `B2bAccountDetailPage` (after Overview, Venues,
 * Claims, Audit log). Composes existing `@bravemove/ui-web` primitives — no new
 * components ship in F13.
 *
 * Read endpoint: `GET /admin/b2b-accounts/:accountId/members` (registered by
 * `apps/api/src/routes/admin/b2b-account-members.ts`, gated by
 * `b2b_account_members:read` — super_admin + support only).
 *
 * The tab itself is rendered only when the actor holds the permission; the
 * parent page guards visibility via the same `permissions` array on
 * `AdminUser`. When permission is missing the tab is hidden entirely (not
 * shown with a 403 banner) so non-privileged admins (finance,
 * content_moderator) never see the tab.
 *
 * Read-only: no action buttons, no row actions. Status filter narrows the
 * displayed rows; the status default is `active` to mirror the F11 admin
 * pages' "show active by default" convention.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Avatar,
  Badge,
  type BadgeTone,
  Banner,
  Chip,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  B2bAccountMember,
  B2bAccountMemberRole,
  B2bAccountMemberStatus,
} from '@bravemove/types';
import { adminFetch } from '../../auth/apiClient.js';
import { translateApiError } from '../../auth/errors.js';

// ─── Display row (joined with user lookup, when available) ────────────────────

export interface MembersListResponse {
  readonly data: {
    readonly items: readonly B2bAccountMember[];
  };
}

// ─── Display maps ─────────────────────────────────────────────────────────────

const ROLE_TONE: Record<B2bAccountMemberRole, BadgeTone> = {
  owner: 'success',
  manager: 'info',
  front_desk: 'neutral',
  accountant: 'warning',
  // F36 — club operational roles (account_kind='club').
  club_admin: 'info',
  club_trainer: 'neutral',
};

const ROLE_LABEL_KEY: Record<B2bAccountMemberRole, string> = {
  owner: 'admin:staff.role.owner',
  manager: 'admin:staff.role.manager',
  front_desk: 'admin:staff.role.front_desk',
  accountant: 'admin:staff.role.accountant',
  // F36 — club operational roles (account_kind='club').
  club_admin: 'admin:staff.role.club_admin',
  club_trainer: 'admin:staff.role.club_trainer',
};

const STATUS_CHIP: Record<B2bAccountMemberStatus, StatusChipStatus> = {
  active: 'approved',
  suspended: 'pending',
  removed: 'rejected',
};

const STATUS_LABEL_KEY: Record<B2bAccountMemberStatus, string> = {
  active: 'admin:staff.status.active',
  suspended: 'admin:staff.status.suspended',
  removed: 'admin:staff.status.removed',
};

const STATUS_FILTERS = ['all', 'active', 'suspended', 'removed'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface B2bAccountMembersTabProps {
  /** B2B account whose members to surface. */
  readonly accountId: string;
  /**
   * When set, client-filter the fetched members to those who can operate THIS
   * venue: keep members whose `venueScope === null` (chain-wide) OR whose
   * `venueScope.includes(venueId)`. Members scoped only to sibling venues are
   * hidden. Omitted on the b2b-account-detail surface → all account members
   * (unchanged behaviour). Used by the admin venue-detail Staff tab.
   */
  readonly venueId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function B2bAccountMembersTab({ accountId, venueId }: B2bAccountMembersTabProps) {
  const { t, locale } = useI18n();
  const [members, setMembers] = useState<readonly B2bAccountMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await adminFetch<MembersListResponse>(
        `/admin/b2b-accounts/${accountId}/members`,
      );
      setMembers(body.data.items);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [accountId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchMembers();
  }, [fetchMembers]);

  // Scope to the venue first (when embedded in the venue-detail Staff tab),
  // then apply the status filter. Chain-wide members (venueScope === null) can
  // operate every venue, so they always survive the venue filter.
  const venueScoped = useMemo(
    () =>
      venueId == null
        ? members
        : members.filter((m) => m.venueScope === null || m.venueScope.includes(venueId)),
    [members, venueId],
  );

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return venueScoped;
    return venueScoped.filter((m) => m.status === statusFilter);
  }, [venueScoped, statusFilter]);

  const columns = useMemo<readonly TableColumn<B2bAccountMember>[]>(
    () => [
      {
        key: 'avatar',
        header: '',
        cell: (row) => <Avatar name={row.userId.slice(0, 2)} size="sm" />,
      },
      {
        key: 'user',
        header: t('admin:staff.list.column_name'),
        cell: (row) => (
          <span className="font-mono text-[11.5px] text-ink-2">
            {row.userId.slice(0, 8)}
            <span aria-hidden="true">…</span>
          </span>
        ),
      },
      {
        key: 'role',
        header: t('admin:staff.list.column_role'),
        cell: (row) => (
          <Badge tone={ROLE_TONE[row.role]} size="sm">
            {t(ROLE_LABEL_KEY[row.role])}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: t('admin:staff.list.column_status'),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP[row.status]}
            label={t(STATUS_LABEL_KEY[row.status])}
          />
        ),
      },
      {
        key: 'scope',
        header: t('admin:staff.list.column_scope'),
        cell: (row) =>
          row.venueScope === null ? (
            <Badge tone="neutral" size="sm">
              {t('admin:staff.list.scope_all_venues')}
            </Badge>
          ) : (
            <span className="text-[12.5px] text-ink-2">
              {t('admin:staff.list.scope_count', { count: row.venueScope.length })}
            </span>
          ),
      },
      {
        key: 'joined',
        header: t('admin:staff.list.column_joined'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">{formatDate(row.acceptedAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  if (loading) {
    return (
      <div data-testid="members-tab-loading" className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return <Banner tone="danger">{error}</Banner>;
  }

  return (
    <div className="space-y-4">
      <p className="text-[12.5px] text-ink-3">{t('admin:staff.tab.subtitle')}</p>

      {/* Status filter chips */}
      <div
        className="flex flex-wrap gap-2"
        role="tablist"
        aria-label={t('admin:staff.filter.aria_label')}
      >
        {STATUS_FILTERS.map((s) => (
          <Chip
            key={s}
            selected={statusFilter === s}
            onClick={() => setStatusFilter(s)}
            role="tab"
            aria-selected={statusFilter === s}
          >
            {s === 'all' ? t('admin:staff.filter.all') : t(STATUS_LABEL_KEY[s])}
          </Chip>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="user" size={36} color="currentColor" />}
          title={t('admin:staff.list.empty_title')}
          description={t('admin:staff.list.empty_body')}
        />
      ) : (
        <Table
          data={filtered as B2bAccountMember[]}
          columns={columns as TableColumn<B2bAccountMember>[]}
          rowKey={(row) => row.id}
        />
      )}
    </div>
  );
}

export default B2bAccountMembersTab;
